import { useState, useEffect } from "react";
import { Plus, Trash2, Pencil, Save, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-[#1A1A1A] border border-white/10 text-white p-3 text-sm focus:border-[#ff0033] outline-none";
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { date: today(), tag_de: "", tag_en: "", title_de: "", title_en: "", body_de: "", body_en: "", image: "" };

// Bild clientseitig verkleinern & als JPEG-DataURL komprimieren (max 1400px, ~0.82 Qualität)
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1400;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const NewsManager = () => {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/news").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY, date: today() }); setEditingId(null); setShow(true); };
  const openEdit = (n) => {
    setForm({ date: n.date || today(), tag_de: n.tag_de || "", tag_en: n.tag_en || "", title_de: n.title_de || "", title_en: n.title_en || "", body_de: n.body_de || "", body_en: n.body_en || "", image: n.image || "" });
    setEditingId(n.id); setShow(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, image: dataUrl }));
    } catch { toast.error("Bild konnte nicht verarbeitet werden"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title_de.trim()) { toast.error("Bitte einen Titel eingeben"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/news/${editingId}`, form);
        toast.success("News aktualisiert");
      } else {
        await api.post("/admin/news", form);
        toast.success("News veröffentlicht");
      }
      setShow(false); setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const del = async (n) => {
    if (!window.confirm(`News "${n.title_de}" wirklich löschen?`)) return;
    await api.delete(`/admin/news/${n.id}`);
    toast.success("News gelöscht"); load();
  };

  return (
    <div data-testid="news-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-4xl uppercase tracking-tight text-white">News-Portal</h1>
          <p className="text-neutral-500 text-sm mt-1">Aktuelle News & Bilder hochladen – erscheinen sofort auf der Home-Seite.</p>
        </div>
        <button onClick={openNew} data-testid="new-news-btn" className="flex items-center gap-2 bg-[#ff0033] text-white font-mono-lab text-xs uppercase tracking-widest px-4 py-3"><Plus size={16} /> Neue News</button>
      </div>

      {show && (
        <div className="bg-[#0F0F0F] border border-[#ff0033]/40 p-5 mb-6" data-testid="news-form">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono-lab text-xs tracking-widest text-[#ff0033] uppercase">{editingId ? "News bearbeiten" : "Neue News"}</p>
            <button onClick={() => { setShow(false); setEditingId(null); }} className="text-neutral-400 hover:text-white"><X size={18} /></button>
          </div>

          {/* Bild-Upload */}
          <div className="mb-4">
            <span className="text-neutral-500 text-xs">Bild</span>
            <div className="mt-1 flex items-start gap-4">
              <div className="w-40 h-28 border border-white/10 bg-[#1A1A1A] flex items-center justify-center overflow-hidden shrink-0">
                {form.image ? <img src={form.image} alt="Vorschau" className="w-full h-full object-cover" data-testid="news-image-preview" /> : <ImageIcon size={26} className="text-neutral-700" />}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 border border-white/20 text-white text-xs uppercase tracking-widest px-4 py-2.5 cursor-pointer hover:border-[#ff0033] hover:text-[#ff0033] transition-colors w-fit">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} Bild wählen
                  <input type="file" accept="image/*" onChange={onImage} className="hidden" data-testid="news-image-input" />
                </label>
                {form.image && <button onClick={() => setForm((f) => ({ ...f, image: "" }))} className="text-neutral-500 hover:text-[#ff0033] text-xs w-fit">Bild entfernen</button>}
                <span className="text-neutral-600 text-[11px]">Wird automatisch verkleinert & optimiert.</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block"><span className="text-neutral-500 text-xs">Datum</span><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="news-date-input" /></label>
            <label className="block"><span className="text-neutral-500 text-xs">Tag / Kategorie (DE)</span><input placeholder="z.B. ERÖFFNUNG" value={form.tag_de} onChange={(e) => setForm({ ...form, tag_de: e.target.value })} className={inputCls} /></label>
            <label className="block"><span className="text-neutral-500 text-xs">Titel (DE)</span><input placeholder="Titel" value={form.title_de} onChange={(e) => setForm({ ...form, title_de: e.target.value })} className={inputCls} data-testid="news-title-input" /></label>
            <label className="block"><span className="text-neutral-500 text-xs">Titel (EN, optional)</span><input placeholder="Title" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={inputCls} /></label>
            <label className="block md:col-span-2"><span className="text-neutral-500 text-xs">Text (DE)</span><textarea rows={4} placeholder="Nachrichtentext" value={form.body_de} onChange={(e) => setForm({ ...form, body_de: e.target.value })} className={inputCls} data-testid="news-body-input" /></label>
            <label className="block"><span className="text-neutral-500 text-xs">Tag / Kategorie (EN, optional)</span><input placeholder="e.g. OPENING" value={form.tag_en} onChange={(e) => setForm({ ...form, tag_en: e.target.value })} className={inputCls} /></label>
            <label className="block"><span className="text-neutral-500 text-xs">Text (EN, optional)</span><textarea rows={2} placeholder="News text" value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} className={inputCls} /></label>
          </div>
          <p className="text-neutral-600 text-[11px] mt-3">Leere EN-Felder werden automatisch mit den deutschen Texten befüllt.</p>
          <button onClick={save} disabled={saving} data-testid="save-news-btn" className="mt-4 w-full flex items-center justify-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {editingId ? "Änderungen speichern" : "News veröffentlichen"}
          </button>
        </div>
      )}

      <div className="space-y-2">
        {list.length === 0 && <p className="text-neutral-500 text-sm">Noch keine News.</p>}
        {list.map((n) => (
          <div key={n.id} className="bg-[#0F0F0F] border border-white/10 p-3 flex items-center gap-4" data-testid={`admin-news-${n.id}`}>
            <div className="w-20 h-16 bg-[#1A1A1A] border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
              {n.image ? <img src={n.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-neutral-700" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white truncate">{n.title_de} {n.tag_de && <span className="text-neutral-500 text-xs">· {n.tag_de}</span>}</p>
              <p className="text-neutral-500 text-xs">{n.date}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button title="Bearbeiten" onClick={() => openEdit(n)} data-testid={`edit-news-${n.id}`} className="p-2 text-neutral-400 hover:text-[#ff0033]"><Pencil size={16} /></button>
              <button title="Löschen" onClick={() => del(n)} data-testid={`delete-news-${n.id}`} className="p-2 text-neutral-400 hover:text-[#ff0033]"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsManager;
